import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import LockIcon from "@material-ui/icons/Lock";
import ReplayIcon from "@material-ui/icons/Replay";
import SaveIcon from "@material-ui/icons/Save";
import WarningIcon from "@material-ui/icons/Warning";
import useTheme from "../../../../theme/useTheme";
import "./ThemeStudio.css";

const colorFields = [
    { key: "primary", label: "Primary" },
    { key: "secondary", label: "Secondary" },
    { key: "accent", label: "Accent" },
    { key: "background", label: "Background" },
    { key: "surface", label: "Surface" },
    { key: "surfaceMuted", label: "Surface Muted" },
    { key: "text", label: "Headline Text" },
    { key: "textMuted", label: "Body Text" },
    { key: "success", label: "Success" },
    { key: "warning", label: "Warning" },
    { key: "danger", label: "Danger" },
    { key: "border", label: "Border" },
];

const typographyFields = [
    {
        key: "fontFamily",
        label: "Base Font Family",
        type: "text",
        placeholder: "'Inter', 'Segoe UI', sans-serif",
    },
    {
        key: "headingFontFamily",
        label: "Heading Font Family",
        type: "text",
        placeholder: "'Clash Display', 'Inter', sans-serif",
    },
    {
        key: "baseFontSize",
        label: "Base Font Size (px)",
        type: "number",
        min: 14,
        max: 20,
    },
    {
        key: "headingScale",
        label: "Heading Scale",
        type: "number",
        step: 0.01,
        min: 1.1,
        max: 1.4,
    },
    {
        key: "lineHeight",
        label: "Line Height",
        type: "number",
        step: 0.01,
        min: 1.4,
        max: 1.8,
    },
];

const layoutFields = [
    {
        key: "contentMaxWidth",
        label: "Content Max Width",
        type: "text",
        placeholder: "1280px",
    },
    {
        key: "sidebarWidth",
        label: "Sidebar Width",
        type: "text",
        placeholder: "320px",
    },
    {
        key: "sectionSpacing",
        label: "Section Spacing",
        type: "text",
        placeholder: "64px",
    },
];

const radiiFields = [
    { key: "sm", label: "Small Radius", type: "text", placeholder: "10px" },
    { key: "md", label: "Medium Radius", type: "text", placeholder: "18px" },
    { key: "lg", label: "Large Radius", type: "text", placeholder: "24px" },
    { key: "pill", label: "Pill Radius", type: "text", placeholder: "999px" },
];

const ThemeStudio = ({ currentUser }) => {
    const {
        tokens,
        theme,
        status,
        error,
        previewTheme,
        clearPreview,
        saveTheme,
    } = useTheme();

    const [draft, setDraft] = useState(tokens);
    const [themeLabel, setThemeLabel] = useState(theme.label || "Aurora Luxe");
    const [preset, setPreset] = useState(theme.preset || "aurora");
    const [feedback, setFeedback] = useState({ type: null, message: "" });
    const [isSaving, setIsSaving] = useState(false);

    const clonedTokens = useMemo(
        () => JSON.parse(JSON.stringify(tokens)),
        [tokens]
    );

    useEffect(() => {
        setDraft(clonedTokens);
        setThemeLabel(theme.label || "Aurora Luxe");
        setPreset(theme.preset || "aurora");
    }, [clonedTokens, theme.label, theme.preset]);

    useEffect(() => {
        return () => {
            clearPreview();
        };
    }, [clearPreview]);

    const isAdmin = currentUser?.isAdmin;
    const unsavedChanges = useMemo(() => {
        return JSON.stringify(draft) !== JSON.stringify(tokens);
    }, [draft, tokens]);

    const handlePaletteChange = (key, value) => {
        const next = {
            ...draft,
            palette: {
                ...draft.palette,
                [key]: value,
            },
        };
        setDraft(next);
        previewTheme({ palette: { [key]: value } });
    };

    const handleTokensChange = (section, key, value) => {
        let transformedValue = value;

        if (["baseFontSize"].includes(key)) {
            transformedValue = Number(value);
        }

        if (["headingScale", "lineHeight"].includes(key)) {
            transformedValue = Number(value);
        }

        const next = {
            ...draft,
            [section]: {
                ...draft[section],
                [key]: transformedValue,
            },
        };

        setDraft(next);
        previewTheme({ [section]: { [key]: transformedValue } });
    };

    const handleReset = () => {
        setDraft(clonedTokens);
        clearPreview();
        setFeedback({ type: "neutral", message: "Reverted to active theme." });
    };

    const handleSave = async () => {
        if (!isAdmin) {
            setFeedback({
                type: "error",
                message: "Only administrators can apply theme changes.",
            });
            return;
        }

        setIsSaving(true);
        setFeedback({ type: null, message: "" });

        try {
            await saveTheme({
                label: themeLabel,
                preset,
                tokens: draft,
                updatedBy: currentUser?._id,
                isAdmin: true,
            });
            setFeedback({ type: "success", message: "Theme updated successfully." });
        } catch (err) {
            setFeedback({
                type: "error",
                message:
                    err?.response?.data?.message ||
                    "Unable to save theme. Please retry in a moment.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="theme-studio__guard">
                <div className="theme-studio__guard-icon">
                    <LockIcon style={{ fontSize: 36 }} />
                </div>
                <h3>Restricted Access</h3>
                <p>
                    The design studio is only available to system administrators. Contact
                    your administrator if you believe you should have access to theme
                    controls.
                </p>
            </div>
        );
    }

    return (
        <div className="theme-studio">
            <div className="theme-studio__column">
                <div className="theme-studio__panel">
                    <div className="theme-studio__panel-header">
                        <h3>Brand Signature</h3>
                        <span className="theme-studio__badge">
                            {unsavedChanges ? "Draft" : "Live"}
                        </span>
                    </div>
                    <div className="theme-studio__grid">
                        <div className="theme-studio__field">
                            <label htmlFor="themeLabel">Theme Label</label>
                            <input
                                id="themeLabel"
                                type="text"
                                value={themeLabel}
                                onChange={(event) => setThemeLabel(event.target.value)}
                                placeholder="Aurora Luxe"
                            />
                        </div>
                        <div className="theme-studio__field">
                            <label htmlFor="preset">Preset</label>
                            <input
                                id="preset"
                                type="text"
                                value={preset}
                                onChange={(event) => setPreset(event.target.value)}
                                placeholder="aurora"
                            />
                        </div>
                    </div>
                    <div className="theme-studio__grid" style={{ marginTop: 18 }}>
                        {colorFields.map(({ key, label }) => (
                            <div className="theme-studio__field" key={key}>
                                <label htmlFor={key}>{label}</label>
                                <input
                                    id={key}
                                    type="color"
                                    value={draft.palette?.[key] || "#000000"}
                                    onChange={(event) =>
                                        handlePaletteChange(key, event.target.value)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="theme-studio__panel">
                    <div className="theme-studio__panel-header">
                        <h3>Typography & Layout</h3>
                    </div>
                    <div className="theme-studio__grid">
                        {typographyFields.map(({ key, label, type, ...rest }) => (
                            <div className="theme-studio__field" key={key}>
                                <label htmlFor={`typography-${key}`}>{label}</label>
                                <input
                                    id={`typography-${key}`}
                                    type={type}
                                    value={
                                        draft.typography?.[key] === undefined
                                            ? ""
                                            : draft.typography[key]
                                    }
                                    onChange={(event) =>
                                        handleTokensChange(
                                            "typography",
                                            key,
                                            event.target.value
                                        )
                                    }
                                    {...rest}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="theme-studio__grid" style={{ marginTop: 18 }}>
                        {layoutFields.map(({ key, label, type, ...rest }) => (
                            <div className="theme-studio__field" key={key}>
                                <label htmlFor={`layout-${key}`}>{label}</label>
                                <input
                                    id={`layout-${key}`}
                                    type={type}
                                    value={
                                        draft.layout?.[key] === undefined
                                            ? ""
                                            : draft.layout[key]
                                    }
                                    onChange={(event) =>
                                        handleTokensChange("layout", key, event.target.value)
                                    }
                                    {...rest}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="theme-studio__grid" style={{ marginTop: 18 }}>
                        {radiiFields.map(({ key, label, type, ...rest }) => (
                            <div className="theme-studio__field" key={key}>
                                <label htmlFor={`radii-${key}`}>{label}</label>
                                <input
                                    id={`radii-${key}`}
                                    type={type}
                                    value={
                                        draft.radii?.[key] === undefined ? "" : draft.radii[key]
                                    }
                                    onChange={(event) =>
                                        handleTokensChange("radii", key, event.target.value)
                                    }
                                    {...rest}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {(unsavedChanges || feedback.type || error) && (
                    <div className="theme-studio__panel">
                        {unsavedChanges && (
                            <div className="theme-studio__status theme-studio__status--unsaved">
                                <WarningIcon />
                                <span>You have unsaved design changes.</span>
                            </div>
                        )}
                        {feedback.type === "success" && (
                            <div className="theme-studio__status">
                                <CheckCircleOutlineIcon />
                                <span>{feedback.message}</span>
                            </div>
                        )}
                        {(feedback.type === "error" || error) && (
                            <div className="theme-studio__status theme-studio__status--error">
                                <WarningIcon />
                                <span>{feedback.message || error}</span>
                            </div>
                        )}
                        {feedback.type === "neutral" && (
                            <div className="theme-studio__status theme-studio__status--unsaved">
                                <span>{feedback.message}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="theme-studio__column">
                <div className="theme-studio__panel">
                    <div className="theme-studio__panel-header">
                        <h3>Live Preview</h3>
                    </div>
                    <div className="theme-studio__preview-card">
                        <div className="theme-studio__preview-chipset">
                            <span className="theme-studio__chip">Premium</span>
                            <span className="theme-studio__chip">Adaptive</span>
                            <span className="theme-studio__chip">Immersive</span>
                        </div>
                        <div className="theme-studio__preview-body">
                            <strong>{themeLabel}</strong>
                            <p>
                                Elevate the library experience with a bespoke visual identity.
                                Adjust color harmonies, typography cadence, and spatial rhythm
                                in real time to match your institution’s signature.
                            </p>
                        </div>
                        <div className="theme-studio__palette-strip">
                            {["primary", "secondary", "accent"].map((key) => (
                                <div
                                    className="theme-studio__palette-swatch"
                                    key={key}
                                    style={{ background: draft.palette?.[key] }}
                                >
                                    <span>{key}</span>
                                    <span>{draft.palette?.[key]}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="theme-studio__panel">
                    <div className="theme-studio__actions">
                        <button
                            type="button"
                            className="theme-studio__button theme-studio__button--ghost"
                            onClick={handleReset}
                            disabled={status === "loading" || isSaving}
                        >
                            <ReplayIcon fontSize="small" />
                            Reset
                        </button>
                        <button
                            type="button"
                            className="theme-studio__button theme-studio__button--primary"
                            onClick={handleSave}
                            disabled={!unsavedChanges || isSaving}
                        >
                            {isSaving ? (
                                <CircularProgress size={18} style={{ color: "var(--color-on-primary)" }} />
                            ) : (
                                <SaveIcon fontSize="small" />
                            )}
                            {isSaving ? "Saving…" : "Save Theme"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThemeStudio;

